import {
  LayoutDashboard, Users, CalendarDays, ListOrdered, Stethoscope, Pill, FlaskConical,
  ScanLine, HeartPulse, ClipboardList, Scissors, BedDouble, Share2, ShieldCheck,
  ReceiptText, Landmark, PackageSearch, Boxes, Video, BriefcaseMedical, CalendarClock,
  Ambulance, Megaphone, MessageSquare, LineChart, KeyRound, UsersRound, ScrollText,
  CreditCard, Settings, Building2, Banknote, CalendarRange, FileMinus2, Activity, Home, Route, Network, Bed,
} from "lucide-react";

// ---- Generic resource module configs (drive list + create/edit forms) ----
const patientField = { name: "patient_id", label: "Patient", type: "patient", required: true };

export const RESOURCES = {
  appointments: {
    title: "Appointments", singular: "Appointment", icon: CalendarDays, endpoint: "/appointments",
    permission: "appointments", canEdit: true,
    columns: [
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "date", label: "Date", type: "datetime" },
      { key: "provider", label: "Provider" },
      { key: "reason", label: "Reason" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      patientField,
      { name: "date", label: "Date & time", type: "datetime", required: true },
      { name: "provider", label: "Provider" },
      { name: "reason", label: "Reason", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: ["scheduled", "confirmed", "completed", "cancelled", "no_show"], default: "scheduled" },
    ],
  },
  queue: {
    title: "Patient Queue", singular: "Queue entry", icon: ListOrdered, endpoint: "/queue",
    permission: "queue", canEdit: true,
    columns: [
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "department", label: "Department" },
      { key: "priority", label: "Priority", type: "badge" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      patientField,
      { name: "department", label: "Department" },
      { name: "priority", label: "Priority", type: "select", options: ["routine", "urgent", "emergency"], default: "routine" },
      { name: "status", label: "Status", type: "select", options: ["waiting", "in_progress", "done"], default: "waiting" },
    ],
  },
  "clinical-records": {
    title: "Clinical Records", singular: "Record", icon: Stethoscope, endpoint: "/clinical-records",
    permission: "clinical", canEdit: false,
    columns: [
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "encounter_type", label: "Encounter" },
      { key: "diagnosis", label: "Diagnosis" },
      { key: "created_at", label: "Date", type: "datetime" },
    ],
    fields: [
      patientField,
      { name: "encounter_type", label: "Encounter type", type: "select", options: ["consultation", "follow_up", "emergency", "admission"], default: "consultation" },
      { name: "complaint", label: "Presenting complaint", type: "textarea" },
      { name: "diagnosis", label: "Diagnosis", type: "textarea" },
      { name: "notes", label: "Clinical notes", type: "textarea" },
    ],
  },
  prescriptions: {
    title: "Prescriptions", singular: "Prescription", icon: Pill, endpoint: "/prescriptions",
    permission: "pharmacy", canEdit: true,
    columns: [
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "drug", label: "Drug" },
      { key: "dosage", label: "Dosage" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      patientField,
      { name: "drug", label: "Drug", required: true },
      { name: "dosage", label: "Dosage" },
      { name: "frequency", label: "Frequency" },
      { name: "duration", label: "Duration" },
      { name: "status", label: "Status", type: "select", options: ["active", "dispensed", "cancelled"], default: "active" },
    ],
  },
  "drug-inventory": {
    title: "Pharmacy Inventory", singular: "Drug", icon: Boxes, endpoint: "/drug-inventory",
    permission: "pharmacy", canEdit: false,
    columns: [
      { key: "name", label: "Drug" },
      { key: "category", label: "Category" },
      { key: "quantity", label: "Qty", type: "number" },
      { key: "unit_price", label: "Unit price", type: "money" },
      { key: "reorder_level", label: "Reorder", type: "number" },
    ],
    fields: [
      { name: "name", label: "Drug name", required: true },
      { name: "category", label: "Category" },
      { name: "quantity", label: "Quantity", type: "number" },
      { name: "unit_price", label: "Unit price", type: "number" },
      { name: "reorder_level", label: "Reorder level", type: "number" },
      { name: "expiry_date", label: "Expiry date", type: "date" },
    ],
  },
  "lab-orders": {
    title: "Laboratory", singular: "Lab order", icon: FlaskConical, endpoint: "/lab-orders",
    permission: "laboratory", canEdit: true,
    columns: [
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "test_name", label: "Test" },
      { key: "priority", label: "Priority", type: "badge" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      patientField,
      { name: "test_name", label: "Test name", required: true },
      { name: "specimen", label: "Specimen" },
      { name: "priority", label: "Priority", type: "select", options: ["routine", "urgent", "stat"], default: "routine" },
      { name: "status", label: "Status", type: "select", options: ["ordered", "collected", "processing", "resulted"], default: "ordered" },
    ],
  },
  ris: {
    title: "Radiology (RIS)", singular: "Imaging order", icon: ScanLine, endpoint: "/ris",
    permission: "radiology", canEdit: true,
    columns: [
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "modality", label: "Modality" },
      { key: "body_part", label: "Body part" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      patientField,
      { name: "modality", label: "Modality", type: "select", options: ["X-Ray", "CT", "MRI", "Ultrasound", "PET-CT"], default: "X-Ray" },
      { name: "body_part", label: "Body part" },
      { name: "reason", label: "Clinical reason", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: ["ordered", "scheduled", "acquired", "reported"], default: "ordered" },
    ],
  },
  "nursing-notes": {
    title: "Nursing & Vitals", singular: "Nursing note", icon: HeartPulse, endpoint: "/nursing-notes",
    permission: "nursing", canEdit: false,
    columns: [
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "temperature", label: "Temp" },
      { key: "bp", label: "BP" },
      { key: "pulse", label: "Pulse" },
      { key: "created_at", label: "Recorded", type: "datetime" },
    ],
    fields: [
      patientField,
      { name: "temperature", label: "Temperature (°C)" },
      { name: "bp", label: "Blood pressure" },
      { name: "pulse", label: "Pulse (bpm)" },
      { name: "spo2", label: "SpO₂ (%)" },
      { name: "note", label: "Note", type: "textarea" },
    ],
  },
  "care-plans": {
    title: "Care Plans", singular: "Care plan", icon: ClipboardList, endpoint: "/care-plans",
    permission: "care_plans", canEdit: true,
    columns: [
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "title", label: "Title" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      patientField,
      { name: "title", label: "Title", required: true },
      { name: "goals", label: "Goals", type: "textarea" },
      { name: "interventions", label: "Interventions", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: ["active", "on_hold", "completed"], default: "active" },
    ],
  },
  "theatre-cases": {
    title: "Theatre / OR", singular: "Theatre case", icon: Scissors, endpoint: "/theatre-cases",
    permission: "theatre", canEdit: true,
    columns: [
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "procedure", label: "Procedure" },
      { key: "surgeon", label: "Surgeon" },
      { key: "scheduled_at", label: "Scheduled", type: "datetime" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      patientField,
      { name: "procedure", label: "Procedure", required: true },
      { name: "surgeon", label: "Surgeon" },
      { name: "theatre", label: "Theatre" },
      { name: "scheduled_at", label: "Scheduled", type: "datetime" },
      { name: "status", label: "Status", type: "select", options: ["scheduled", "in_theatre", "recovery", "completed", "cancelled"], default: "scheduled" },
    ],
  },
  admissions: {
    title: "Inpatient / Admissions", singular: "Admission", icon: BedDouble, endpoint: "/admissions",
    permission: "inpatient", canEdit: false,
    columns: [
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "ward", label: "Ward" },
      { key: "bed", label: "Bed" },
      { key: "status", label: "Status", type: "badge" },
      { key: "admitted_at", label: "Admitted", type: "datetime" },
    ],
    fields: [
      patientField,
      { name: "ward", label: "Ward" },
      { name: "bed", label: "Bed" },
      { name: "reason", label: "Reason", type: "textarea" },
    ],
  },
  referrals: {
    title: "Referrals", singular: "Referral", icon: Share2, endpoint: "/referrals",
    permission: "referrals", canEdit: true,
    columns: [
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "specialty", label: "Specialty" },
      { key: "to_provider", label: "To" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      patientField,
      { name: "specialty", label: "Specialty" },
      { name: "to_provider", label: "Refer to" },
      { name: "reason", label: "Reason", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: ["pending", "accepted", "completed", "rejected"], default: "pending" },
    ],
  },
  "insurance-claims": {
    title: "Insurance Claims", singular: "Claim", icon: ShieldCheck, endpoint: "/insurance-claims",
    permission: "insurance", canEdit: true,
    columns: [
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "insurer", label: "Insurer" },
      { key: "amount", label: "Amount", type: "money" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      patientField,
      { name: "insurer", label: "Insurer", required: true },
      { name: "policy_number", label: "Policy number" },
      { name: "amount", label: "Amount", type: "number" },
      { name: "status", label: "Status", type: "select", options: ["draft", "submitted", "approved", "rejected", "paid"], default: "draft" },
    ],
  },
  "inventory-items": {
    title: "Inventory", singular: "Item", icon: PackageSearch, endpoint: "/inventory-items",
    permission: "inventory", canEdit: true,
    columns: [
      { key: "name", label: "Item" },
      { key: "category", label: "Category" },
      { key: "quantity", label: "Qty", type: "number" },
      { key: "unit", label: "Unit" },
    ],
    fields: [
      { name: "name", label: "Item name", required: true },
      { name: "category", label: "Category" },
      { name: "quantity", label: "Quantity", type: "number" },
      { name: "unit", label: "Unit" },
      { name: "reorder_level", label: "Reorder level", type: "number" },
    ],
  },
  "purchase-orders": {
    title: "Procurement", singular: "Purchase order", icon: BriefcaseMedical, endpoint: "/purchase-orders",
    permission: "procurement", canEdit: true,
    columns: [
      { key: "supplier", label: "Supplier" },
      { key: "total", label: "Total", type: "money" },
      { key: "status", label: "Status", type: "badge" },
      { key: "created_at", label: "Date", type: "date" },
    ],
    fields: [
      { name: "supplier", label: "Supplier", required: true },
      { name: "item", label: "Item" },
      { name: "quantity", label: "Quantity", type: "number" },
      { name: "total", label: "Total", type: "number" },
      { name: "status", label: "Status", type: "select", options: ["draft", "sent", "received", "cancelled"], default: "draft" },
    ],
  },
  "tele-consults": {
    title: "Telemedicine", singular: "Tele-consult", icon: Video, endpoint: "/tele-consults",
    permission: "telemedicine", canEdit: true,
    columns: [
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "provider", label: "Provider" },
      { key: "scheduled_at", label: "Scheduled", type: "datetime" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      patientField,
      { name: "provider", label: "Provider" },
      { name: "scheduled_at", label: "Scheduled", type: "datetime" },
      { name: "link", label: "Meeting link" },
      { name: "status", label: "Status", type: "select", options: ["scheduled", "completed", "cancelled"], default: "scheduled" },
    ],
  },
  "emergency-visits": {
    title: "Emergency / ED", singular: "ED visit", icon: Ambulance, endpoint: "/emergency-visits",
    permission: "emergency", canEdit: true,
    columns: [
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "triage", label: "Triage", type: "badge" },
      { key: "complaint", label: "Complaint" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      patientField,
      { name: "complaint", label: "Complaint", type: "textarea" },
      { name: "triage", label: "Triage", type: "select", options: ["red", "orange", "yellow", "green", "blue"], default: "yellow" },
      { name: "status", label: "Status", type: "select", options: ["waiting", "in_treatment", "admitted", "discharged"], default: "waiting" },
    ],
  },
  "leave-requests": {
    title: "Leave Requests", singular: "Leave request", icon: CalendarClock, endpoint: "/leave-requests",
    permission: "workforce", canEdit: true,
    columns: [
      { key: "reason", label: "Reason" },
      { key: "start_date", label: "From", type: "date" },
      { key: "end_date", label: "To", type: "date" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      { name: "leave_type", label: "Type", type: "select", options: ["annual", "sick", "study", "compassionate"], default: "annual" },
      { name: "start_date", label: "Start date", type: "date", required: true },
      { name: "end_date", label: "End date", type: "date", required: true },
      { name: "reason", label: "Reason", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: ["pending", "approved", "rejected"], default: "pending" },
    ],
  },
  "staff-schedules": {
    title: "Staff Schedules", singular: "Shift", icon: CalendarClock, endpoint: "/staff-schedules",
    permission: "workforce", canEdit: true,
    columns: [
      { key: "staff_name", label: "Staff" },
      { key: "shift", label: "Shift" },
      { key: "date", label: "Date", type: "date" },
      { key: "department", label: "Department" },
    ],
    fields: [
      { name: "staff_name", label: "Staff name", required: true },
      { name: "date", label: "Date", type: "date" },
      { name: "shift", label: "Shift", type: "select", options: ["morning", "afternoon", "night"], default: "morning" },
      { name: "department", label: "Department" },
    ],
  },
  notices: {
    title: "Notice Board", singular: "Notice", icon: Megaphone, endpoint: "/notices",
    permission: "communication", canEdit: false,
    columns: [
      { key: "title", label: "Title" },
      { key: "body", label: "Message" },
      { key: "created_at", label: "Posted", type: "datetime" },
    ],
    fields: [
      { name: "title", label: "Title", required: true },
      { name: "body", label: "Message", type: "textarea" },
      { name: "audience", label: "Audience", type: "select", options: ["all", "clinical", "admin"], default: "all" },
    ],
  },
  enquiries: {
    title: "CRM / Enquiries", singular: "Enquiry", icon: MessageSquare, endpoint: "/enquiries",
    permission: "crm", canEdit: true,
    columns: [
      { key: "name", label: "Name" },
      { key: "subject", label: "Subject" },
      { key: "channel", label: "Channel" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      { name: "name", label: "Name", required: true },
      { name: "contact", label: "Phone / email" },
      { name: "subject", label: "Subject" },
      { name: "message", label: "Message", type: "textarea" },
      { name: "channel", label: "Channel", type: "select", options: ["walk_in", "phone", "email", "web"], default: "walk_in" },
      { name: "status", label: "Status", type: "select", options: ["new", "in_progress", "resolved"], default: "new" },
    ],
  },
  wards: {
    title: "Wards", singular: "Ward", icon: BedDouble, endpoint: "/wards",
    permission: "inpatient", readonly: true,
    columns: [
      { key: "name", label: "Ward" },
      { key: "type", label: "Type" },
      { key: "capacity", label: "Beds", type: "number" },
      { key: "occupied", label: "Occupied", type: "number" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [],
  },
  beds: {
    title: "Beds", singular: "Bed", icon: Bed, endpoint: "/beds",
    permission: "inpatient", readonly: true,
    columns: [
      { key: "bed_number", label: "Bed", render: (r) => r.bed_number || r.label || r.name || r.id },
      { key: "ward_name", label: "Ward", render: (r) => r.ward_name || r.ward || r.ward_id || "—" },
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [],
  },
  "bank-accounts": {
    title: "Bank Accounts", singular: "Bank account", icon: Banknote, endpoint: "/bank-accounts",
    permission: "finance", canEdit: false,
    columns: [
      { key: "bank_name", label: "Bank" },
      { key: "account_name", label: "Account name" },
      { key: "account_number", label: "Account no." },
      { key: "currency", label: "Currency" },
      { key: "purpose", label: "Purpose", type: "badge" },
    ],
    fields: [
      { name: "bank_name", label: "Bank name", required: true },
      { name: "account_name", label: "Account name", required: true },
      { name: "account_number", label: "Account number", required: true },
      { name: "sort_code", label: "Sort / branch code" },
      { name: "currency", label: "Currency", type: "select", options: ["NGN", "GHS", "KES", "ZAR", "GBP", "USD", "EUR"], default: "NGN" },
      { name: "purpose", label: "Purpose", type: "select", options: ["collections", "payroll", "operations", "nhis_hmo"], default: "collections" },
    ],
  },
  "payment-plans": {
    title: "Payment Plans", singular: "Payment plan", icon: CalendarRange, endpoint: "/payment-plans",
    permission: "billing", canEdit: true,
    columns: [
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "total_amount", label: "Total", type: "money" },
      { key: "installments", label: "Instalments", type: "number" },
      { key: "frequency", label: "Frequency" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      patientField,
      { name: "invoice_id", label: "Invoice", type: "remote", endpoint: () => "/invoices", labelOf: (i) => `${i.invoice_number || i.id} · ${i.total ?? ""}` },
      { name: "total_amount", label: "Total amount", type: "number", required: true },
      { name: "deposit", label: "Deposit paid", type: "number" },
      { name: "installments", label: "Number of instalments", type: "number", required: true },
      { name: "frequency", label: "Frequency", type: "select", options: ["weekly", "fortnightly", "monthly"], default: "monthly" },
      { name: "start_date", label: "First instalment", type: "date" },
      { name: "status", label: "Status", type: "select", options: ["active", "completed", "defaulted", "cancelled"], default: "active" },
    ],
  },
  "credit-notes": {
    title: "Credit Notes", singular: "Credit note", icon: FileMinus2, endpoint: "/credit-notes",
    permission: "billing", canEdit: false,
    columns: [
      { key: "credit_note_number", label: "No.", render: (r) => r.credit_note_number || r.number || r.id },
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "amount", label: "Amount", type: "money" },
      { key: "reason", label: "Reason" },
      { key: "created_at", label: "Issued", type: "date" },
    ],
    fields: [
      patientField,
      { name: "invoice_id", label: "Against invoice", type: "remote", endpoint: () => "/invoices", labelOf: (i) => `${i.invoice_number || i.id} · ${i.total ?? ""}` },
      { name: "amount", label: "Credit amount", type: "number", required: true },
      { name: "reason", label: "Reason", type: "select", options: ["overcharge", "refund", "service_not_rendered", "insurance_adjustment", "goodwill"], default: "overcharge" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
  "device-readings": {
    title: "Device Readings (RPM)", singular: "Reading", icon: Activity, endpoint: "/device-readings",
    permission: "nursing", canEdit: false,
    columns: [
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "device_type", label: "Device", type: "badge" },
      { key: "value", label: "Value", render: (r) => `${r.value ?? "—"} ${r.unit || ""}` },
      { key: "recorded_at", label: "Recorded", type: "datetime", render: undefined },
    ],
    fields: [
      patientField,
      { name: "device_type", label: "Device", type: "select", options: ["bp_monitor", "glucometer", "pulse_oximeter", "thermometer", "weight_scale", "ecg"], default: "bp_monitor" },
      { name: "value", label: "Reading value", required: true },
      { name: "unit", label: "Unit (e.g. mmHg, mmol/L, %)" },
      { name: "recorded_at", label: "Recorded at", type: "datetime" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
  "homecare-visits": {
    title: "Home Healthcare", singular: "Home healthcare visit", icon: Home, endpoint: "/homecare-visits",
    permission: "nursing", canEdit: true,
    columns: [
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "scheduled_at", label: "Scheduled", type: "datetime" },
      { key: "assigned_to", label: "Clinician" },
      { key: "address", label: "Address" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      patientField,
      { name: "scheduled_at", label: "Visit date & time", type: "datetime", required: true },
      { name: "address", label: "Home address", type: "textarea" },
      { name: "assigned_to", label: "Assigned clinician" },
      { name: "purpose", label: "Purpose", type: "select", options: ["wound_care", "medication", "post_discharge", "palliative", "physiotherapy", "vitals_check"], default: "post_discharge" },
      { name: "notes", label: "Visit notes", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: ["scheduled", "en_route", "completed", "cancelled"], default: "scheduled" },
    ],
  },
  "care-pathways": {
    title: "Patient Journey", singular: "Patient journey", icon: Route, endpoint: "/care-pathways",
    permission: "care_plans", canEdit: true,
    columns: [
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "pathway", label: "Pathway" },
      { key: "current_stage", label: "Stage" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      patientField,
      { name: "pathway", label: "Pathway", type: "select", options: ["antenatal", "diabetes", "hypertension", "hiv_art", "tb_dots", "oncology", "stroke", "sickle_cell"], default: "antenatal" },
      { name: "current_stage", label: "Current stage" },
      { name: "start_date", label: "Start date", type: "date" },
      { name: "next_review", label: "Next review", type: "date" },
      { name: "notes", label: "Notes", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: ["active", "on_hold", "completed", "exited"], default: "active" },
    ],
  },
  "partner-referrals": {
    title: "Partner Network Referrals", singular: "Partner referral", icon: Network, endpoint: "/partner-referrals",
    permission: "external_referrals", canEdit: false,
    columns: [
      { key: "patient_name", label: "Patient", ref: "patient" },
      { key: "kind", label: "Type", type: "badge" },
      { key: "partner_name", label: "Partner", render: (r) => r.partner_name || r.partner_org_name || r.partner_org_id || "—" },
      { key: "tests_requested", label: "Requested" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      { name: "kind", label: "Partner type", type: "select", options: ["lab", "radiology"], default: "lab", required: true },
      { name: "partner_org_id", label: "Partner centre", type: "remote", required: true, dependsOn: "kind",
        endpoint: (f) => (f.kind ? `/partner-directory?kind=${f.kind}` : null),
        labelOf: (p) => `${p.name}${p.accreditation ? ` · ${p.accreditation}` : ""}${p.turnaround_days ? ` · ${p.turnaround_days}d TAT` : ""}` },
      patientField,
      { name: "tests_requested", label: "Tests / studies requested", type: "textarea", required: true },
      { name: "clinical_notes", label: "Clinical notes", type: "textarea" },
      { name: "priority", label: "Priority", type: "select", options: ["routine", "urgent", "stat"], default: "routine" },
    ],
    actions: [
      { key: "accept", label: "Accept", show: (r) => ["pending", "sent", "new"].includes(r.status), path: (r) => `/partner-referrals/${r.id}/accept` },
      { key: "result", label: "Submit result", show: (r) => ["accepted", "in_progress"].includes(r.status), path: (r) => `/partner-referrals/${r.id}/result`,
        fields: [
          { name: "result", label: "Result / report", type: "textarea", required: true },
          { name: "result_url", label: "Report link (optional)" },
        ] },
    ],
  },
  "audit-logs": {
    title: "Audit Trail", singular: "Log", icon: ScrollText, endpoint: "/audit-logs",
    permission: "audit", readonly: true,
    columns: [
      { key: "action", label: "Action" },
      { key: "actor", label: "Actor" },
      { key: "entity", label: "Entity" },
      { key: "created_at", label: "When", type: "datetime" },
    ],
    fields: [],
  },
};

// ---- Sidebar navigation groups ----
export const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/app/patients", label: "Patients", icon: Users, permission: "patients" },
      { to: "/app/appointments", label: "Appointments", icon: CalendarDays, permission: "appointments" },
      { to: "/app/queue", label: "Queue", icon: ListOrdered, permission: "queue" },
    ],
  },
  {
    label: "Clinical",
    items: [
      { to: "/app/clinical-records", label: "Clinical / EMR", icon: Stethoscope, permission: "clinical" },
      { to: "/app/nursing-notes", label: "Nursing & Vitals", icon: HeartPulse, permission: "nursing" },
      { to: "/app/care-plans", label: "Care Plans", icon: ClipboardList, permission: "care_plans" },
      { to: "/app/emergency-visits", label: "Emergency / ED", icon: Ambulance, permission: "emergency" },
      { to: "/app/theatre-cases", label: "Theatre / OR", icon: Scissors, permission: "theatre" },
      { to: "/app/admissions", label: "Inpatient", icon: BedDouble, permission: "inpatient" },
      { to: "/app/wards", label: "Wards", icon: BedDouble, permission: "inpatient" },
      { to: "/app/beds", label: "Beds", icon: Bed, permission: "inpatient" },
      { to: "/app/care-pathways", label: "Patient Journey", icon: Route, permission: "care_plans" },
      { to: "/app/homecare-visits", label: "Home Healthcare", icon: Home, permission: "nursing" },
      { to: "/app/device-readings", label: "Device Readings", icon: Activity, permission: "nursing" },
    ],
  },
  {
    label: "Diagnostics & Pharmacy",
    items: [
      { to: "/app/lab-orders", label: "Laboratory", icon: FlaskConical, permission: "laboratory" },
      { to: "/app/ris", label: "Radiology", icon: ScanLine, permission: "radiology" },
      { to: "/app/prescriptions", label: "Prescriptions", icon: Pill, permission: "pharmacy" },
      { to: "/app/drug-inventory", label: "Pharmacy Stock", icon: Boxes, permission: "pharmacy" },
    ],
  },
  {
    label: "Coordination",
    items: [
      { to: "/app/referrals", label: "Referrals", icon: Share2, permission: "referrals" },
      { to: "/app/partner-referrals", label: "Partner Network", icon: Network, permission: "external_referrals" },
      { to: "/app/tele-consults", label: "Telemedicine", icon: Video, permission: "telemedicine" },
    ],
  },
  {
    label: "Revenue",
    items: [
      { to: "/app/billing", label: "Billing & Invoices", icon: ReceiptText, permission: "billing" },
      { to: "/app/payment-plans", label: "Payment Plans", icon: CalendarRange, permission: "billing" },
      { to: "/app/credit-notes", label: "Credit Notes", icon: FileMinus2, permission: "billing" },
      { to: "/app/bank-accounts", label: "Bank Accounts", icon: Banknote, permission: "finance" },
      { to: "/app/insurance-claims", label: "Insurance Claims", icon: ShieldCheck, permission: "insurance" },
      { to: "/app/inventory-items", label: "Inventory", icon: PackageSearch, permission: "inventory" },
      { to: "/app/purchase-orders", label: "Procurement", icon: BriefcaseMedical, permission: "procurement" },
    ],
  },
  {
    label: "Workforce & CRM",
    items: [
      { to: "/app/staff-schedules", label: "Schedules", icon: CalendarClock, permission: "workforce" },
      { to: "/app/leave-requests", label: "Leave", icon: CalendarClock, permission: "workforce" },
      { to: "/app/notices", label: "Notice Board", icon: Megaphone, permission: "communication" },
      { to: "/app/enquiries", label: "CRM / Enquiries", icon: MessageSquare, permission: "crm" },
    ],
  },
  {
    label: "Administration",
    items: [
      { to: "/app/analytics", label: "Analytics", icon: LineChart, permission: "analytics" },
      { to: "/app/staff", label: "Staff & Invites", icon: UsersRound, permission: "staff_admin" },
      { to: "/app/rbac", label: "Roles & Access", icon: KeyRound, permission: "rbac_admin" },
      { to: "/app/audit-logs", label: "Audit Trail", icon: ScrollText, permission: "audit" },
      { to: "/app/subscription", label: "Subscription", icon: CreditCard },
      { to: "/app/settings", label: "Organisation", icon: Building2 },
    ],
  },
];

export const ALL_NAV_ITEMS = NAV_GROUPS.flatMap((g) => g.items);
